import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { Button, IconButton, LocalizedDate, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft } from 'lucide-react';

import {
  $dateRange,
  $deductionTypes,
  $loadsPendingReconciliation,
  $searchTerm,
  $selectedFacility,
} from '../../_state/loadReconciliation.state';
import { useTranslations } from '../../i18n/useTranslations';
import type {
  LoadRecord,
  LoadsTabListItem,
  WeightUnitType,
} from '../../types/load';
import { DetailViewHeader } from '../_shared/DetailViewHeader';
import { LoadListItem } from '../_shared/LoadsTab/LoadListItem';
import { MaterialsReconciliation } from '../_shared/MaterialsReconciliation';
import { ReconciliationFooter } from '../_shared/MaterialsReconciliation/ReconciliationFooter';

import { LoadStatusTransition } from './LoadStatusTransition';

import { WeightUnitSwitch } from '~/modules/trade/_components/_shared/WeightUnitSwitch';
import { useDebouncedValue } from '~/shared/hooks/useDebounce';
import {
  $defaultUnitSystemUnit,
  $salesDefaultWeightDisplayUnit,
} from '~/shared/state/settings.state';

const toLoadsTabListItem = (load: LoadRecord): LoadsTabListItem => ({
  id: load.id,
  loadNumber: load.loadNumber,
  status: load.status,
  reviewed: load.reviewed,
  customerName: load.customerName ?? load.salesOrder?.customerName ?? undefined,
  shippingAddress: load.salesOrder?.shippingAddress ?? null,
  materialItemIds: load.lineItems
    ?.map((item) => item.itemId)
    .filter(Boolean) as string[],
  lineItemCount: load.lineItemCount ?? load.lineItems?.length,
});

type LoadReconciliationTableProps = {
  onBack: () => void;
  searchTerm?: string;
  dateRange?: {
    start: CalendarDate;
    end: CalendarDate;
  } | null;
  selectedFacility?: string | null;
  initialSelectedLoad?: LoadRecord | null;
};

export const LoadReconciliationTable: React.FC<
  LoadReconciliationTableProps
> = ({
  onBack,
  searchTerm: initialSearchTerm = '',
  dateRange: initialDateRange,
  selectedFacility: initialSelectedFacility,
  initialSelectedLoad,
}) => {
  const t = useTranslations();
  const [selectedLoadId, setSelectedLoadId] = React.useState<string | null>(
    initialSelectedLoad?.id ?? null,
  );
  const [searchTerm, setSearchTerm] = React.useState(initialSearchTerm);
  const setDateRange = useSetAtom($dateRange);
  const setInternalSearchTerm = useSetAtom($searchTerm);
  const setSelectedFacility = useSetAtom($selectedFacility);

  // Update internal search term from external with a debounce
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  React.useEffect(() => {
    setInternalSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm, setInternalSearchTerm]);

  React.useEffect(() => {
    setSearchTerm(initialSearchTerm);
  }, [initialSearchTerm]);

  React.useEffect(() => {
    setDateRange(initialDateRange ?? null);
  }, [initialDateRange, setDateRange]);

  React.useEffect(() => {
    setSelectedFacility(initialSelectedFacility ?? null);
  }, [initialSelectedFacility, setSelectedFacility]);

  const { data, isLoading } = useAtomValue($loadsPendingReconciliation);
  const { data: deductionTypesData } = useAtomValue($deductionTypes);

  // Weight unit settings - use local state so MaterialsReconciliation and ReconciliationFooter share the same state
  const facilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);
  const defaultWeightDisplayUnit = useAtomValue($salesDefaultWeightDisplayUnit);
  const [weightUnit, setWeightUnit] = React.useState<WeightUnitType>(
    defaultWeightDisplayUnit,
  );

  // Update local state when settings change
  React.useEffect(() => {
    setWeightUnit(defaultWeightDisplayUnit);
  }, [defaultWeightDisplayUnit]);

  const filteredLoads = React.useMemo(() => {
    if (!data?.loads) {
      return [];
    }
    return data.loads;
  }, [data?.loads]);

  // Update selectedLoadId when initialSelectedLoad changes
  React.useEffect(() => {
    if (initialSelectedLoad?.id) {
      setSelectedLoadId(initialSelectedLoad.id);
    }
  }, [initialSelectedLoad?.id]);

  React.useEffect(() => {
    if (!selectedLoadId && filteredLoads.length > 0) {
      setSelectedLoadId(filteredLoads[0].id ?? null);
    }
  }, [filteredLoads, selectedLoadId]);

  const selectedLoad = React.useMemo(
    () => data?.loads.find((load) => load.id === selectedLoadId),
    [data?.loads, selectedLoadId],
  );

  if (isLoading && !data) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
          <div className="h-24 rounded bg-neutral/20"></div>
          <div className="h-24 rounded bg-neutral/20"></div>
        </div>
      </div>
    );
  }

  if (data?.loads.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-neutral/5 p-6">
        <div className="text-center">
          <h3 className="text-lg font-medium text-neutral/90">
            {t.loadReconciliation.emptyState.title}
          </h3>
          <p className="mt-1 text-sm text-neutral/50">
            {t.loadReconciliation.emptyState.subtitle}
          </p>
          <div className="mt-6">
            <Button variant="outline" color="neutral" onPress={onBack}>
              <ArrowLeft className="mr-2 -ml-1 size-5" />
              {t.loadReconciliation.emptyState.backButton}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="@LoadReconciliationTable flex h-full bg-neutral/5">
      <div className="h-full w-[250px] flex-shrink-0 overflow-y-auto border-r border-neutral/20 bg-white">
        <div className="p-4">
          <div className="flex items-center gap-2">
            <IconButton
              variant="outline"
              color="neutral"
              icon={<ArrowLeft className="size-4" />}
              onPress={onBack}
              className="h-[40px] w-[40px]"
            />
            <TextField
              placeholder={t.loadReconciliation.search.placeholder}
              value={searchTerm}
              onChange={setSearchTerm}
              className="flex-1"
            />
          </div>
        </div>

        <div className="px-4 pb-4 text-sm text-warning-darker">
          <span className="font-semibold">
            {t.loadReconciliation.listTitle}
          </span>{' '}
          ({filteredLoads.length})
        </div>

        {filteredLoads.map((load) => (
          <LoadListItem
            key={load.id}
            load={toLoadsTabListItem(load)}
            isSelected={selectedLoadId === load.id}
            onClick={() => setSelectedLoadId(load.id ?? null)}
          />
        ))}
      </div>

      {/* Materials Reconciliation Panel */}
      <div className="@DetailsWrapper flex h-full flex-col overflow-x-auto px-7.5">
        {selectedLoad && deductionTypesData ? (
          <>
            <DetailViewHeader
              seamless
              title={t.loadDetail.title(
                (selectedLoad as LoadRecord).loadNumber,
              )}
              subtitle={
                <>
                  {t.loadDetail.created}{' '}
                  <LocalizedDate date={selectedLoad?.timestamps?.createdAt} />
                </>
              }
              actions={
                <>
                  {selectedLoad.lineItems &&
                    selectedLoad.lineItems.length > 0 && (
                      <WeightUnitSwitch
                        value={weightUnit}
                        onChange={setWeightUnit}
                      />
                    )}
                  <LoadStatusTransition
                    load={selectedLoad as LoadRecord}
                    hideVoidButton
                    disabled={false}
                    skipMaterialsReconciliation={true}
                    displayDetailButton={true}
                  />
                </>
              }
            />
            <div className="h-full overflow-y-auto">
              <MaterialsReconciliation
                load={selectedLoad as LoadRecord}
                deductionTypes={deductionTypesData}
                dialogMode={false}
                weightUnit={weightUnit}
                facilityDefaultUnit={facilityDefaultUnit}
                onWeightUnitChange={setWeightUnit}
                showWeightUnitSwitch={false}
              />
              {/* Reconciliation Footer Card */}

              <ReconciliationFooter
                weightUnit={weightUnit}
                facilityDefaultUnit={facilityDefaultUnit}
              />
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-neutral/50">{t.loadReconciliation.selectLoad}</p>
          </div>
        )}
      </div>
    </div>
  );
};
